import React from "react";
import { Modal } from "../../overlay/modals/Modal";
import { Button } from "../../primitives/button";
import { TextInput } from "../TextInput/TextInput";

interface LinkDialogProps {
  isOpen: boolean;
  /** The link already on the selection, or "" when adding a new one. */
  initialUrl: string;
  onClose: () => void;
  onSubmit: (url: string) => void;
  onRemove?: () => void;
}

/**
 * Where a link's address is typed.
 *
 * A dialog rather than `window.prompt`: the native one can't be styled, is
 * blocked outright in some browsers, and gives no room to say what a valid
 * address looks like — an admin who typed "acme.com/badges" got a link to a
 * page relative to Tactic with nothing to explain why.
 */
export const LinkDialog: React.FC<LinkDialogProps> = ({
  isOpen,
  initialUrl,
  onClose,
  onSubmit,
  onRemove
}) => {
  const [url, setUrl] = React.useState(initialUrl);

  // Reset on each open: the dialog stays mounted between uses, so a URL left
  // over from the previous link would otherwise be offered for the next one.
  React.useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
    }
  }, [isOpen, initialUrl]);

  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialUrl ? "Edit link" : "Add link"}
      size="md"
    >
      <TextInput
        label="Address"
        helperText="Starts with https:// for a web page, mailto: for an email address, or tel: for a phone number."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        // The dialog opens on a click in the toolbar, so nothing else is
        // competing for focus and typing can start straight away.
        autoFocus
        placeholder="https://example.com"
      />

      <Modal.Footer>
        {onRemove ? (
          <Button
            variant="outline"
            onClick={onRemove}
            className="mr-auto text-red-600 hover:text-red-700"
          >
            Remove link
          </Button>
        ) : null}
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={url.trim().length === 0}>
          {initialUrl ? "Update" : "Add link"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
